// useBenefitToggle.ts (Custom Hook)

import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {createEisTypeObject, isDefined} from 'Quoting/utils/utilities';
import {Map as ImmutableMap, fromJS} from 'immutable';
import {dependentChildOfDislocationRider, fractureDefaultValues, reductionDetailsDefaults} from './benefitDefaults';
import {getCalculatedBenefitAmounts, getMoneyObject} from './GAI7';
import {benefitOptions} from '../../MappingPaths';


export const useBenefitToggle = () => {
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);

  const planRootId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.rootId`);
  const coverageDefId = getQuoteFieldValue(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[0]._key.rootId`
  );

  const onToggleBenefitApply = (
    benefitEntityName: string,
    benefitAppliedPath: string,
    value: boolean,
    benefitDetailsPath: string
  ) => {
    const fieldsToUpdate: {path: string, newValue: any}[] = [
      {
        path: benefitAppliedPath,
        newValue: value,
      },
    ];

    if (!isDefined(getQuoteFieldValue(benefitDetailsPath))) {
      const benefitDetails = getBenefitDetailsObject(
        benefitEntityName,
        defaultPlanValues?.get('GAI7'),
        planRootId ?? '',
        coverageDefId ?? ''
      );

      fieldsToUpdate.push({
        path: benefitDetailsPath,
        newValue: fromJS(benefitDetails),
      });
    }

    updateQuoteFieldValues(fieldsToUpdate);
  };

  const getBenefitDetailsObject = (
    benefitEntityName: string,
    defaultValues: ImmutableMap<string, any> | undefined,
    planRootId: string,
    coverageDefId: string
  ) => {
    switch (benefitEntityName) {
      case 'ACCStepDownIntensiveCareUnitConfinementBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCStepDownIntensiveCareUnitConfinementBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get(
            'ACCStepDownIntensiveCareUnitConfinementBenefit.customizeBenefitAmount'
          ),
          benefitAmount: defaultValues?.get('ACCStepDownIntensiveCareUnitConfinementBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCStepDownIntensiveCareUnitConfinementBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCStepDownIntensiveCareUnitConfinementBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCStepDownIntensiveCareUnitConfinementBenefit', planRootId, coverageDefId),
        };
      case 'ACCRehabilitationFacilityBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCRehabilitationFacilityBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get('ACCRehabilitationFacilityBenefit.customizeBenefitAmount'),
          benefitAmount: defaultValues?.get('ACCRehabilitationFacilityBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCRehabilitationFacilityBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCRehabilitationFacilityBenefit.timePeriodCd'),
          maxBenefitNumber2: defaultValues?.get('ACCRehabilitationFacilityBenefit.maxBenefitNumber2'),
          timePeriod2Cd: defaultValues?.get('ACCRehabilitationFacilityBenefit.timePeriod2Cd'),
          ...createEisTypeObject('ACCRehabilitationFacilityBenefit', planRootId, coverageDefId),
        };
      case 'ACCPunctureWoundBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCPunctureWoundBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get('ACCPunctureWoundBenefit.customizeBenefitAmount'),
          benefitAmount: defaultValues?.get('ACCPunctureWoundBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCPunctureWoundBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCPunctureWoundBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCPunctureWoundBenefit', planRootId, coverageDefId),
        };
      case 'ACCGunshotWoundBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCGunshotWoundBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get('ACCGunshotWoundBenefit.customizeBenefitAmount'),
          benefitAmount: defaultValues?.get('ACCGunshotWoundBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCGunshotWoundBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCGunshotWoundBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCGunshotWoundBenefit', planRootId, coverageDefId),
        };
      case 'ACCFollowUpTreatmentBenefit':
        return {
          units: defaultValues?.get('ACCFollowUpTreatmentBenefit.units'),
          incurralPeriod: defaultValues?.get('ACCFollowUpTreatmentBenefit.incurralPeriod'),
          isBenefitAmountCustomize: defaultValues?.get('ACCFollowUpTreatmentBenefit.isBenefitAmountCustomize'),
          benefitAmount: defaultValues?.get('ACCFollowUpTreatmentBenefit.standartBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCFollowUpTreatmentBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCFollowUpTreatmentBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCFollowUpTreatmentBenefit', planRootId, coverageDefId),
        };
      case 'ACCXRayBenefit':
        return {
          units: defaultValues?.get('ACCXRayBenefit.units'),
          incurralPeriod: defaultValues?.get('ACCXRayBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get('ACCXRayBenefit.customizeBenefitAmount'),
          benefitAmount: defaultValues?.get('ACCXRayBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCXRayBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCXRayBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCXRayBenefit', planRootId, coverageDefId),
        };
      case 'ACCEmergencyRoomServicesBenefit':
        return {
          unitsOfEmergencyRoomServices: defaultValues?.get(
            'ACCEmergencyRoomServicesBenefit.unitsOfEmergencyRoomServices'
          ),
          incurralPeriod: defaultValues?.get('ACCEmergencyRoomServicesBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get('ACCEmergencyRoomServicesBenefit.customizeBenefitAmount'),
          benefitAmount: defaultValues?.get('ACCEmergencyRoomServicesBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCEmergencyRoomServicesBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCEmergencyRoomServicesBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCEmergencyRoomServicesBenefit', planRootId, coverageDefId),
        };
      case 'ACCPainManagementBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCPainManagementBenefit.incurralPeriod'),
          benefitAmount: defaultValues?.get('ACCPainManagementBenefit.standardBenefitAmount'),
          epiduralInjection: defaultValues?.get('ACCPainManagementBenefit.epiduralInjection'),
          nerveAblationProcedure: defaultValues?.get('ACCPainManagementBenefit.nerveAblationProcedure'),
          maxBenefitNumber: defaultValues?.get('ACCPainManagementBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCPainManagementBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCPainManagementBenefit', planRootId, coverageDefId),
        };

      case 'ACCPainManagementCorticosteroidInjectionBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCPainManagementCorticosteroidInjectionBenefit.incurralPeriod'),
          customizeBenefitAmount: defaultValues?.get(
            'ACCPainManagementCorticosteroidInjectionBenefit.customizeBenefitAmount'
          ),
          benefitAmount: defaultValues?.get('ACCPainManagementCorticosteroidInjectionBenefit.standardBenefitAmount'),
          maxBenefitNumber: defaultValues?.get('ACCPainManagementCorticosteroidInjectionBenefit.maxBenefitNumber'),
          timePeriodCd: defaultValues?.get('ACCPainManagementCorticosteroidInjectionBenefit.timePeriodCd'),
          ...createEisTypeObject('ACCPainManagementCorticosteroidInjectionBenefit', planRootId, coverageDefId),
        };

      case 'ACCAutomobileAccidentalDeathBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCAutomobileAccidentalDeathBenefit.incurralPeriod'),
          benefitAmount: defaultValues?.get('ACCAutomobileAccidentalDeathBenefit.standardBenefitAmount'),
          ...createEisTypeObject('ACCAutomobileAccidentalDeathBenefit', planRootId, coverageDefId),
        };
      case 'ACCDislocationFractureRiderDetails':
        return {
          scheduleOfDislocationRider: defaultValues?.get(
            'ACCDislocationFractureRiderDetails.scheduleOfDislocationRider'
          ),
          dependentChildOfDislocationRider: dependentChildOfDislocationRider,
          timesMaximumClosedBenefit: defaultValues?.get('ACCDislocationFractureRiderDetails.timesMaximumClosedBenefit'),
          timePeroidMaximumClosedBenefit: defaultValues?.get(
            'ACCDislocationFractureRiderDetails.timePeroidMaximumClosedBenefit'
          ),
          timesMaximumOpenBenefit: defaultValues?.get('ACCDislocationFractureRiderDetails.timesMaximumOpenBenefit'),
          timePeroidMaximumOpenBenefit: defaultValues?.get(
            'ACCDislocationFractureRiderDetails.timePeroidMaximumOpenBenefit'
          ),
          // maximumClosedReductionBenefitAmount: getCalculatedBenefitAmounts(2000),
          // maximumOpenReductionBenefitAmount: getCalculatedBenefitAmounts(6000),
          ...getCalculatedBenefitAmounts(defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1), //remove later
          ...createEisTypeObject('ACCDislocationFractureRiderDetails', planRootId, coverageDefId),
        };

      case 'ACCDislocationBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCDislocationBenefit.incurralPeriod'),
          partialDislocation: defaultValues?.get('ACCDislocationBenefit.partialDislocation'),
          reductionAmountDetails: [
            // Open Reduction Amount
            {
              hipAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.hipStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              kneeNoPatellaAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              footBonesAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.footBonesStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              ankleAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.ankleStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              wristAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.wristStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              elbowAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.elbowStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              shoulderGlenohumeralAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              handBonesAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.handBonesStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              collarboneAcromioclavicularAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              twoOrMoreFingersAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              twoOrMoreToesAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              oneFingerOrToeAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              kneecapAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.kneecapStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              lowerJawAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.lowerJawStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              otherDislocationAmount: getMoneyObject(
                3 *
                  reductionDetailsDefaults.otherDislocationStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              ...createEisTypeObject('ACCDislocationReductionAmt', planRootId, coverageDefId),
            },
            // Closed Reduction Amount
            {
              hipAmount: getMoneyObject(
                reductionDetailsDefaults.hipStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              kneeNoPatellaAmount: getMoneyObject(
                reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              footBonesAmount: getMoneyObject(
                reductionDetailsDefaults.footBonesStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              ankleAmount: getMoneyObject(
                reductionDetailsDefaults.ankleStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              wristAmount: getMoneyObject(
                reductionDetailsDefaults.wristStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              elbowAmount: getMoneyObject(
                reductionDetailsDefaults.elbowStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              shoulderGlenohumeralAmount: getMoneyObject(
                reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              handBonesAmount: getMoneyObject(
                reductionDetailsDefaults.handBonesStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              collarboneAcromioclavicularAmount: getMoneyObject(
                reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              twoOrMoreFingersAmount: getMoneyObject(
                reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              twoOrMoreToesAmount: getMoneyObject(
                reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              oneFingerOrToeAmount: getMoneyObject(
                reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              kneecapAmount: getMoneyObject(
                reductionDetailsDefaults.kneecapStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              lowerJawAmount: getMoneyObject(
                reductionDetailsDefaults.lowerJawStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              otherDislocationAmount: getMoneyObject(
                reductionDetailsDefaults.otherDislocationStandardBenefitAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfDislocationRider') || 1)
              ),
              ...createEisTypeObject('ACCDislocationReductionAmt', planRootId, coverageDefId),
            },
          ],
          ...createEisTypeObject('ACCDislocationBenefit', planRootId, coverageDefId),
        };

      case 'ACCFractureBenefit':
        return {
          incurralPeriod: defaultValues?.get('ACCFractureBenefit.incurralPeriod'),
          reductionAmountDetails: [
            {
              hipAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.hipThighFemurFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              skullFractureNonDepressedNoFaceNoseBonesAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.skullFractureNonDepressedAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.pelvisFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              upperArmBetwElbowAndShoulderHumerusAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              shoulderBladeScapulaAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.shoulderBladeFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              legTibiaFibulaAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.legFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              ankleAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.ankleFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              kneecapPatellaAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.kneecapFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              collarboneClavicleSternumAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.collarBoneFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              forearmRadiusUlnaAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.forearmFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              footOrHeelNoToesAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.footFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              handNoFingersAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.handOrWristFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              lowerJawMandibleNoAlveolarProcessAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.lowerJawFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              twoOrMoreRibsFingersOrToesAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              faceOrNoseBonesNoMandibleOrMaxillaAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.bonesOfFaceFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              noseAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.noseFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              oneRibFingerOrToeAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.oneRibFingerToeFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              coccyxAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.coccyxFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              skullFractureDepressedNoFaceNoseBonesAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.skullFractureDepressedAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              vertebraeBodyOfNoVertebralProcessesAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.vertebralBodyFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              vertebralProcessesAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.vertebralProcessFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              upperJawMaxillaNoAlveolarProcessAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.upperJawFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              sternumAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.sternumFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              anyOtherFractureAmount: getMoneyObject(
                3 *
                  fractureDefaultValues.anyOtherFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              ...createEisTypeObject('ACCFractureReductionAmt', planRootId, coverageDefId),
            },
            {
              hipAmount: getMoneyObject(
                fractureDefaultValues.hipThighFemurFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              skullFractureNonDepressedNoFaceNoseBonesAmount: getMoneyObject(
                fractureDefaultValues.skullFractureNonDepressedAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount: getMoneyObject(
                fractureDefaultValues.pelvisFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              upperArmBetwElbowAndShoulderHumerusAmount: getMoneyObject(
                fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              shoulderBladeScapulaAmount: getMoneyObject(
                fractureDefaultValues.shoulderBladeFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              legTibiaFibulaAmount: getMoneyObject(
                fractureDefaultValues.legFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              ankleAmount: getMoneyObject(
                fractureDefaultValues.ankleFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              kneecapPatellaAmount: getMoneyObject(
                fractureDefaultValues.kneecapFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              collarboneClavicleSternumAmount: getMoneyObject(
                fractureDefaultValues.collarBoneFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              forearmRadiusUlnaAmount: getMoneyObject(
                fractureDefaultValues.forearmFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              footOrHeelNoToesAmount: getMoneyObject(
                fractureDefaultValues.footFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              handNoFingersAmount: getMoneyObject(
                fractureDefaultValues.handOrWristFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              lowerJawMandibleNoAlveolarProcessAmount: getMoneyObject(
                fractureDefaultValues.lowerJawFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              twoOrMoreRibsFingersOrToesAmount: getMoneyObject(
                fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              faceOrNoseBonesNoMandibleOrMaxillaAmount: getMoneyObject(
                fractureDefaultValues.bonesOfFaceFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              noseAmount: getMoneyObject(
                fractureDefaultValues.noseFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              oneRibFingerOrToeAmount: getMoneyObject(
                fractureDefaultValues.oneRibFingerToeFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              coccyxAmount: getMoneyObject(
                fractureDefaultValues.coccyxFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              skullFractureDepressedNoFaceNoseBonesAmount: getMoneyObject(
                fractureDefaultValues.skullFractureDepressedAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              vertebraeBodyOfNoVertebralProcessesAmount: getMoneyObject(
                fractureDefaultValues.vertebralBodyFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              vertebralProcessesAmount: getMoneyObject(
                fractureDefaultValues.vertebralProcessFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              upperJawMaxillaNoAlveolarProcessAmount: getMoneyObject(
                fractureDefaultValues.upperJawFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              sternumAmount: getMoneyObject(
                fractureDefaultValues.sternumFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              anyOtherFractureAmount: getMoneyObject(
                fractureDefaultValues.anyOtherFractureAmount *
                  (defaultValues?.get('ACCBenefitStructure.unitsOfFractureRider') || 1)
              ),
              ...createEisTypeObject('ACCFractureReductionAmt', planRootId, coverageDefId),
            },
          ],
          chipFracture: defaultValues?.get('ACCFractureBenefit.chipFracture'),
          stressFractures: defaultValues?.get('ACCFractureBenefit.stressFractures'),
          ...createEisTypeObject('ACCFractureBenefit', planRootId, coverageDefId),
        };
      default:
        return {};
    }
  };

  return {
    onToggleBenefitApply,
    getBenefitDetailsObject,
  };
};

export const isStateApplicableForHSS = (state: string | undefined): boolean => {
  // List of applicable states for health care services rider
  const applicableStates: string[] = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'DE',
    'FL',
    'GA',
    'GU',
    'HI',
    'IL',
    'IA',
    'LA',
    'MA',
    'MS',
    'NV',
    'NC',
    'OK',
    'PA',
    'SC',
    'SD',
    'TX',
    'WV',
    'WI',
    'WY',
    'CA',
    'NJ',
    'NE',
    'TN',
    'VT',
    'MT',
    'CT',
    'ME',
    'ND',
    'PR',
    'UT',
    'VI',
    'MO',
    'OH',
    'KS'
  ];

  return state === undefined ? false : applicableStates.includes(state);
};

export const isStateApplicableForOPT = (state: string | undefined): boolean => {
  // List of applicable states for Outpatient Physician Treatments
  const applicableStates: string[] = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'DE',
    'FL',
    'GA',
    'GU',
    'HI',
    'IL',
    'IA',
    'LA',
    'MA',
    'MS',
    'NV',
    'NC',
    'OK',
    'PA',
    'SC',
    'SD',
    'TX',
    'WV',
    'WI',
    'WY',
    'CA',
    'NJ',
    'NE',
    'TN',
    'VA',
    'NH',
    'UT',
    'ME',
    'MT',
    'ND',
    'PR',
    'VI',
    'VT',
    'CT',
  ];

  return state === undefined ? false : applicableStates.includes(state);
};


const ACCExclWillfulCriminalActivity = 'Willful criminal activity (MI Only)';

export const getBenefitLabelMappingforGAI7 = (
  benefitName: string,
  parentPath: string,
  keyFlag?: {type: string; flag: string},
  situsStateCd?: string
) => {
  const filteredExclusions = Object.entries(benefitOptions[benefitName]).filter(([k, v]) => {
    const isCommonExclusion = v && v !== ACCExclWillfulCriminalActivity;
    const isMIExclusion = situsStateCd === 'MI' && v === ACCExclWillfulCriminalActivity;
    return isCommonExclusion || isMIExclusion;
  });

  return filteredExclusions.map(([k, v], newIndex) => {
    const exclusionPath = keyFlag ? `${parentPath}.${keyFlag.type}[${newIndex}].${keyFlag.flag}` : `${parentPath}.${k}`;
   
    return {label: v, path: exclusionPath};
  });
};
